import React, { useState, useEffect } from 'react';
import { MdLocationOn } from "react-icons/md";
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { Chip } from "@mui/material";
import "./ListingItem.scss";

export default function ListingItem({ listing, onEdit, onDelete, onClick, mortgage1, useStreetView }) {
  // Determine listing type based on the presence of formattedAddress.
  const isOnMarket = listing.formattedAddress !== undefined;
  const isOffMarket = !isOnMarket;

  // On-market: fetch a street view image.
  const [imageSrc, setImageSrc] = useState(null);
  useEffect(() => {
    if (isOnMarket) {
      if (useStreetView) {
        // Instead of fetching the image as a blob, set the URL directly.
        const timestamp = new Date().getTime();
        const streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${listing.latitude},${listing.longitude}&key=AIzaSyDVTNByoOWDFyw4OS55EaOzJ6yzpxYjKi8&timestamp=${timestamp}`;
        setImageSrc(streetViewUrl);
      } else {
        setImageSrc(listing.imgSrc);
      }
    }
  }, [isOnMarket, listing.latitude, listing.longitude, useStreetView, listing.imgSrc]);
  


  // Favorite state.
  const [isFavorited, setIsFavorited] = useState(false);
  const toggleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorited(prev => !prev);
  };

  // Calculate FMR for on-market listings.
  let fmr = 0;
  if (isOnMarket) {
    const OneBedFMR = 1207;
    const TwoBedFMR = 1355;
    const ThreeBedFMR = 1781;
    const FourBedFMR = 2086;
    if (listing.bedrooms === 1) fmr = OneBedFMR;
    else if (listing.bedrooms === 2) fmr = TwoBedFMR;
    else if (listing.bedrooms === 3) fmr = ThreeBedFMR;
    else if (listing.bedrooms > 3) fmr = FourBedFMR;
  }

  // Off-market modals state.
  const [showOffMarketModal, setShowOffMarketModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // ---------------------------
  // Modal style objects

  // Off Market Details Modal styles:
  const offMarketModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const offMarketModalContentStyle = {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    textAlign: 'center',
  };

  // Full Address (Disclaimer) Modal styles:
  const addressModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const addressModalContentStyle = {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    textAlign: 'center',
  };

  const addressModalTextStyle = {
    fontSize: '16px',
    marginBottom: '20px',
  };

  const addressModalButtonStyle = {
    padding: '10px 20px',
    fontSize: '16px',
    backgroundColor: agreed ? '#007aff' : '#ccc',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: agreed ? 'pointer' : 'not-allowed',
  };

  // Button style for "Show Full Address" (only on off-market listings):
  const showAddressButtonStyle = {
    marginTop: '10px',
    padding: '8px 12px',
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  };

  // ---------------------------
  // Render on-market vs. off-market

  if (isOnMarket) {
    // On-market branch (original working code):
    return (
      <li className="listingItem" onClick={onClick}>
        <img
          className="listingItem__image"
          loading="lazy"
          src={imageSrc}
          alt="house"
        />
        <div
          className="listingItem__favorite-icon"
          onClick={toggleFavorite}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          {isFavorited ? <FaHeart color="red" size={24} /> : <FaRegHeart color="white" size={24} />}
        </div>
      
        <div className="listingItem__details">
          <div className="listingItem__wrap">
            <MdLocationOn className="listingItem__location-icon" />
            <p className="listingItem__location-address">{listing.formattedAddress}</p>
          </div>
          <p className="listingItem__name">{listing.name}</p>
          <p className="listingItem__price">
            ${Number(listing.price).toLocaleString()}
          </p>
          {listing.propertyType && (
            (listing.propertyType === "Land" || listing.propertyType === "Condo") ? 
              <Chip label={listing.propertyType} style={{ backgroundColor: 'white', border: '2px solid', color:'#457b9d', width: 100, height: 30, fontSize: 12, fontWeight: 600, marginTop: '5px' }}/>
            :
              <Chip label={listing.propertyType} style={{ backgroundColor: 'white', border: '2px solid', color:'#457b9d', width: 130, height: 30, fontSize: 12, fontWeight: 600, marginTop: '5px' }}/>
          )}
          <p className="listingItem__price">
            FMR: ${fmr} &nbsp;&nbsp; Payment: ${Math.round(mortgage1)}
          </p>
          <div className="listingItem__pricewrap">
            <p className="listingItem__price1">
              {(fmr - Math.round(mortgage1)) <= 0 ? (
                <Chip label={`Cashflow: $${fmr - Math.round(mortgage1)}`} style={{ backgroundColor: 'red', color:'white', width:130, height:30, fontSize:12, fontWeight:600, marginTop:'5px' }}/>
              ) : ((fmr - Math.round(mortgage1)) > 0 && (fmr - Math.round(mortgage1)) < 200) ? (
                <Chip label={`Cashflow: $${fmr - Math.round(mortgage1)}`} style={{ backgroundColor: '#FED000', color:'black', width:130, height:30, fontSize:12, fontWeight:600, marginTop:'5px' }}/>
              ) : (
                <Chip label={`Cashflow: $${fmr - Math.round(mortgage1)}`} style={{ backgroundColor: 'green', color:'white', width:130, height:30, fontSize:12, fontWeight:600, marginTop:'5px' }}/>
              )}
            </p>
          </div>
          <div className="listingItem__wrap listingItem__wrap--increase-margin">
            <div className="listingItem__wrap">
              {listing.bedrooms > 0 && <p className="listingItem__bath-and-bed">{listing.bedrooms} Bedrooms</p>}
            </div>
            <div className="listingItem__wrap">
              {listing.bathrooms > 0 && <p className="listingItem__bath-and-bed">{listing.bathrooms} Baths</p>}
            </div>
          </div>
        </div>
      </li>
    );
  } else {
    // Off-market branch: use listing.image and include the "Show Full Address" button.
    return (
      <>
        <li
          className="listingItem"
          onClick={() => onClick(listing)}         
           style={{ position: 'relative' }} // Ensure the container is relatively positioned
        >
          <img
            className="listingItem__image"
            loading="lazy"
            src={listing.image}
            alt="property"
          />
          {/* Badge for off-market listing */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '5px 8px',
              borderRadius: '4px',
              zIndex: 10,
              fontSize: '12px',
            }}
          >
            {listing.dateRef}
          </div>
          <div
            className="listingItem__favorite-icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(e);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              cursor: 'pointer',
              zIndex: 10,
            }}
          >
            {isFavorited ? <FaHeart color="red" size={24} /> : <FaRegHeart color="white" size={24} />}
          </div>
          <div className="listingItem__details">
            <div className="listingItem__wrap">
              <MdLocationOn className="listingItem__location-icon" />
              <p className="listingItem__location-address">{listing.address}</p>
            </div>
            <p className="listingItem__name">{listing.address}</p>
            <p className="listingItem__price">{listing.price}</p>
            {listing.arv && <p className="listingItem__arv">{listing.arv}</p>}
            <p className="listingItem__beds-baths">
              {listing.beds} | {listing.baths}
            </p>
            {listing.squareFeet && <p className="listingItem__squareFeet">{listing.squareFeet}</p>}
            {/* "Show Full Address" button appears only on off-market listings */}
            <button 
              style={showAddressButtonStyle} 
              onClick={(e) => {
                e.stopPropagation();
                setShowAddressModal(true);
              }}
            >
              Show Full Address
            </button>
          </div>
        </li>
        {/* Full Address (Disclaimer) Modal */}
        {showAddressModal && (
          <div style={addressModalOverlayStyle} onClick={() => setShowAddressModal(false)}>
            <div style={addressModalContentStyle} onClick={(e) => e.stopPropagation()}>
              <p style={addressModalTextStyle}>
                I agree to conduct all inquiries into and discussions about this property solely through the Disposition Manager and shall not directly contact the seller. Any unauthorized contact with the seller will be considered intentional interference with a Contract. I agree to pay damages of $75,000 to the contract holder and $25,000 to Realsync LLC if I attempt to circumvent or intentionally interfere with this Contract.
              </p>
              <label style={{ display: 'block', marginBottom: '20px' }}>
                <input 
                  type="checkbox" 
                  checked={agreed} 
                  onChange={(e) => setAgreed(e.target.checked)} 
                />
                {' '}I agree to terms
              </label>
              <button 
                style={addressModalButtonStyle} 
                onClick={() => { if(agreed) { setShowAddressModal(false); setAgreed(false); } }}
                disabled={!agreed}
              >
                Show Full Address
              </button>
            </div>
          </div>
        )}
        {/* Off Market Details Modal */}
        {showOffMarketModal && (
          <div style={offMarketModalOverlayStyle} onClick={() => setShowOffMarketModal(false)}>
            <div style={offMarketModalContentStyle} onClick={(e) => e.stopPropagation()}>
              <img 
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/8MxPEv2dda8JikaeCS0U/media/67a2e4495de7ca8170c76393.png" 
                alt="Off Market Details"
                style={{ width: '100%', borderRadius: '8px', marginBottom: '15px' }}
              />
              <p>Off Market Details coming soon!</p>
              <button 
                onClick={() => setShowOffMarketModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007aff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
}
